import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";

/**
 * Botão que ativa/desativa notificações push do admin.
 * Estados:
 *  - Não suportado (browser sem PushManager) → não renderiza
 *  - Permissão "denied" → botão desabilitado com dica
 *  - Sem subscription → "Ativar notificações"
 *  - Com subscription → "Notificações ativas" (clique desativa)
 */
function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = window.atob((b64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function PushToggle({ variant = "icon" }) {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const checkSubscription = useCallback(async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(!!sub);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    setPermission(Notification.permission);
    checkSubscription();
  }, [checkSubscription]);

  const ativar = async () => {
    setLoading(true);
    try {
      let perm = Notification.permission;
      if (perm !== "granted") {
        perm = await Notification.requestPermission();
        setPermission(perm);
      }
      if (perm !== "granted") {
        toast.error("Permissão de notificações negada pelo navegador.");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      const subJson = sub.toJSON();
      await api.post("/push/subscribe", { endpoint: subJson.endpoint, keys: subJson.keys });
      setSubscribed(true);
      toast.success("Notificações ativadas! Você receberá alertas de novas solicitações.");
    } catch (e) {
      console.error("push subscribe error", e);
      toast.error(e?.response?.data?.detail || "Não foi possível ativar as notificações.");
    } finally {
      setLoading(false);
    }
  };

  const desativar = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try { await api.post("/push/unsubscribe", { endpoint }); } catch { /* ignore */ }
      }
      setSubscribed(false);
      toast.success("Notificações desativadas.");
    } catch (e) {
      toast.error("Erro ao desativar notificações.");
    } finally {
      setLoading(false);
    }
  };

  if (!supported) return null;

  const denied = permission === "denied";
  const onClick = subscribed ? desativar : ativar;
  const label = denied
    ? "Bloqueadas pelo navegador"
    : subscribed
    ? "Notificações ativas"
    : "Ativar notificações";
  const Icon = denied ? BellOff : subscribed ? BellRing : Bell;

  if (variant === "full") {
    return (
      <button
        type="button"
        onClick={denied ? undefined : onClick}
        disabled={loading || denied}
        data-testid="push-toggle-full"
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition ${
          denied
            ? "bg-zinc-100 text-zinc-400 cursor-not-allowed"
            : subscribed
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
            : "bbh-btn-primary"
        }`}
      >
        <Icon className="w-4 h-4" />
        {loading ? "Aguarde…" : label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={denied ? undefined : onClick}
      disabled={loading || denied}
      title={label}
      aria-label={label}
      data-testid="push-toggle"
      className={`inline-flex items-center justify-center w-10 h-10 rounded-full border transition ${
        denied
          ? "border-zinc-200 text-zinc-300 cursor-not-allowed"
          : subscribed
          ? "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
          : "border-zinc-200 text-zinc-500 hover:text-zinc-800"
      }`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}
